import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { getQueryParams } from "@/utils/httpRequest";
import { formatFileSize } from "@/utils/fileUtils";
import { DEFAULT_NOTIFICATION_PAYLOAD, EMPTY_GUID, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { showNotification } from "@/redux/actions/notification";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";

export const loanSummaryService = createApi({
  reducerPath: "loanSummaryApi",
  tagTypes: ["LoanSummary", "LoanSummaryNeedListFile", "LoanSummaryLenders"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLoanSummaries: builder.query({
      query: ({ loanId, ...params }) => `loans/${loanId}/summaries?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ loanSummaryId }) => ({ type: "LoanSummary", id: loanSummaryId })),
              { type: "LoanSummary", id: "LIST" },
            ]
          : [{ type: "LoanSummary", id: "LIST" }],
    }),
    getLendersByLoanSummaryId: builder.query({
      query: ({ loanId, loanSummaryId }) => `loans/${loanId}/summaries/${loanSummaryId}/lenders`,
      providesTags: (result, api, queryArgs) =>
        result
          ? [
              { type: "LoanSummaryLenders", id: queryArgs.loanSummaryId },
              { type: "LoanSummaryLenders", id: "LIST" },
            ]
          : [{ type: "LoanSummaryLenders", id: "LIST" }],
    }),
    getLoanSummary: builder.query({
      query: ({ loanId, loanSummaryId }) => `loans/${loanId}/summaries/${loanSummaryId}`,
      providesTags: (result, error, { loanSummaryId }) => [{ type: "LoanSummary", id: loanSummaryId }],
    }),
    getLoanSummaryDraft: builder.query({
      query: ({ loanId, draftId }) => `loans/${loanId}/summaries/drafts/${draftId}`,
      providesTags: (result, error, { draftId }) => [{ type: "LoanSummary", draftId }],
    }),
    addLoanSummary: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/summaries`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "LoanSummary", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_SUMMARY_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Loan summary has been created!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    addLoanSummaryDraft: builder.mutation({
      query: ({ loanId, loanSummaryId, ...body }) => ({
        url: `loans/${loanId}/summaries/${loanSummaryId}/drafts`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "LoanSummary", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_SUMMARY_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Loan summary preview is generated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateLoanSummary: builder.mutation({
      query: ({ loanId, loanSummaryId, ...body }) => ({
        url: `loans/${loanId}/summaries/${loanSummaryId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { loanSummaryId }) => [
        { type: "LoanSummary", id: loanSummaryId },
        { type: "LoanSummary", id: "LIST" },
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_SUMMARY_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Loan package has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLoanSummary: builder.mutation({
      query({ loanId, loanSummaryId }) {
        return {
          url: `loans/${loanId}/summaries/${loanSummaryId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "LoanSummary", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_SUMMARY_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Loan summary has been deleted!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          dispatch(setProcessingState(false));
          Bugsnag.notify(err);
        }
      },
    }),
    getLoanSummaryNeedListFiles: builder.query({
      query: ({ loanId, loanSummaryId, ...params }) =>
        `loans/${loanId}/summaries/${loanSummaryId || EMPTY_GUID}/needs-list?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              // ...result.map(({ needsListFileId }) => ({ type: "LoanSummary", needsListFileId })),
              { type: "LoanSummaryNeedListFile", id: "LIST" },
            ]
          : [{ type: "LoanSummaryNeedListFile", id: "LIST" }],
      transformResponse: items => {
        // First, group the items by borrower and document
        const grouped = items.reduce((item, file) => {
          const records = { ...item };
          if (!item[file.borrowerId]) {
            records[file.borrowerId] = {
              borrowerName: file.borrowerName,
              documents: {
                [file.needListId]: {
                  documentName: file.documentName,
                  files: [],
                },
              },
            };
          }

          if (!records[file.borrowerId].documents[file.needListId]) {
            records[file.borrowerId].documents[file.needListId] = {
              documentName: file.documentName,
              files: [],
            };
          }

          records[file.borrowerId].documents[file.needListId].files.push({
            loanId: file.loanId,
            loanSummaryId: file.loanSummaryId,
            borrowerId: file.borrowerId,
            needListId: file.needListId,
            needListFileId: file.needListFileId,
            fileName: file.fileName,
            fileSize: file.fileSize,
            fileType: file.fileType,
            receivedAt: file.receivedAt,
            sentAt: file.sentAt,
            isActive: file.isActive,
          });

          return records;
        }, {});

        // Transform grouped data into tree format
        const treeNodes = [];

        Object.entries(grouped).forEach(([borrowerId, borrowerData]) => {
          const borrowerNode = {
            id: borrowerId,
            text: borrowerData.borrowerName,
            hasChildren: true,
            iconCss: "user",
            child: [],
            type: "borrower",
            payload: {
              ...borrowerData,
            },
          };

          Object.entries(borrowerData.documents).forEach(([needListId, documentData]) => {
            const documentNode = {
              id: needListId,
              text: documentData.documentName,
              hasChildren: documentData.files.length > 0,
              iconCss: "folder",
              child: [],
              type: "document",
              payload: {
                ...documentData,
              },
            };

            documentData.files.forEach(file => {
              const fileNode = {
                id: file.needListFileId,
                text: `${file.fileName}${file.fileSize ? ` (${formatFileSize(file.fileSize)})` : ""}`,
                hasChildren: false,
                iconCss: "document",
                type: "file",
                isChecked: file.isActive,
                payload: {
                  ...file,
                },
              };

              documentNode.child.push(fileNode);
            });

            borrowerNode.child.push(documentNode);
          });

          treeNodes.push(borrowerNode);
        });

        return treeNodes;
      },
    }),
    addLoanSummaryNeedList: builder.mutation({
      query: ({ loanId, loanSummaryId, borrowerId, needListId, needListFileId }) => ({
        url: `loans/${loanId}/summaries/${loanSummaryId}/needs-list/${needListId}/borrowers/${borrowerId}/files/${needListFileId}`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "LoanSummary", id: "LIST" }],
      async onQueryStarted(
        { loanId, loanSummaryId, borrowerId, needListId, needListFileId },
        { dispatch, queryFulfilled, getState },
      ) {
        // Get all existing query cache entries for this endpoint
        const queryCacheKeys = Object.keys(getState().loanSummaryApi.queries).filter(key =>
          key.startsWith(`getLoanSummaryNeedListFiles({"loanId":"${loanId}","loanSummaryId":"${loanSummaryId}"`),
        );

        // Apply optimistic update to all matching cache entries
        const patchResults = queryCacheKeys.map(cacheKey => {
          const queryArgs = JSON.parse(cacheKey.match(/\((.+)\)/)[1]);
          return dispatch(
            loanSummaryService.util.updateQueryData("getLoanSummaryNeedListFiles", queryArgs, draft => {
              // Find the borrower node in the tree
              const borrowerNode = draft.find(node => node.id === borrowerId && node.type === "borrower");
              if (borrowerNode) {
                // Find the document node
                const documentNode = borrowerNode.child.find(
                  node => node.id === needListId && node.type === "document",
                );
                if (documentNode) {
                  // Find the file node and update its isChecked status
                  const fileNode = documentNode.child.find(node => node.id === needListFileId && node.type === "file");
                  if (fileNode) {
                     
                    fileNode.isChecked = true;
                    // Also update the payload data
                    if (fileNode.payload) {
                       
                      fileNode.payload.isActive = true;
                      fileNode.payload.sentAt = new Date();
                    }
                  }
                }
              }
            }),
          );
        });

        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_SUMMARY_NEEDS_LIST_FILE_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File added to loan summary!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          // Revert optimistic update on error
          patchResults.forEach(result => result.undo());
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLoanSummaryNeedList: builder.mutation({
      query: ({ loanId, loanSummaryId, borrowerId, needListId, needListFileId }) => ({
        url: `loans/${loanId}/summaries/${loanSummaryId}/needs-list/${needListId}/borrowers/${borrowerId}/files/${needListFileId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "LoanSummary", id: "LIST" }],
      async onQueryStarted(
        { loanId, loanSummaryId, borrowerId, needListId, needListFileId },
        { dispatch, queryFulfilled, getState },
      ) {
        // Get all existing query cache entries for this endpoint
        const queryCacheKeys = Object.keys(getState().loanSummaryApi.queries).filter(key =>
          key.startsWith(`getLoanSummaryNeedListFiles({"loanId":"${loanId}","loanSummaryId":"${loanSummaryId}"`),
        );

        // Apply optimistic update to all matching cache entries
        const patchResults = queryCacheKeys.map(cacheKey => {
          const queryArgs = JSON.parse(cacheKey.match(/\((.+)\)/)[1]);
          return dispatch(
            loanSummaryService.util.updateQueryData("getLoanSummaryNeedListFiles", queryArgs, draft => {
              // Find the borrower node in the tree
              const borrowerNode = draft.find(node => node.id === borrowerId && node.type === "borrower");
              if (borrowerNode) {
                // Find the document node
                const documentNode = borrowerNode.child.find(
                  node => node.id === needListId && node.type === "document",
                );
                if (documentNode) {
                  // Find the file node and update its isChecked status
                  const fileNode = documentNode.child.find(node => node.id === needListFileId && node.type === "file");
                  if (fileNode) {
                     
                    fileNode.isChecked = false;
                    // Also update the payload data
                    if (fileNode.payload) {
                       
                      fileNode.payload.isActive = false;
                      fileNode.payload.sentAt = null;
                    }
                  }
                }
              }
            }),
          );
        });

        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_SUMMARY_NEEDS_LIST_FILE_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File delete from loan summary!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          // Revert optimistic update on error
          patchResults.forEach(result => result.undo());
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useGetLoanSummariesQuery,
  useGetLendersByLoanSummaryIdQuery,
  useGetLoanSummaryQuery,
  useGetLoanSummaryDraftQuery,
  useAddLoanSummaryMutation,
  useAddLoanSummaryDraftMutation,
  useUpdateLoanSummaryMutation,
  useDeleteLoanSummaryMutation,
  useGetLoanSummaryNeedListFilesQuery,
  useAddLoanSummaryNeedListMutation,
  useDeleteLoanSummaryNeedListMutation,
} = loanSummaryService;
